import cn from "classnames";
import dayjs, { Dayjs } from "dayjs";

import { useEffect, useMemo, useRef, useState } from "react";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import FlightClassOutlinedIcon from "@mui/icons-material/FlightClassOutlined";
import FlightLandOutlinedIcon from "@mui/icons-material/FlightLandOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { LoadingButton } from "@mui/lab";
import {
  Checkbox,
  Fab,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import CheckboxGroup from "@/components/CheckboxGroup";
import SelectPassengerV2 from "@/components/SelectPassengerV2";
import {
  AlertTypeEnum,
  FlightWayEnum,
  cabinOptionMap,
  flightWayOptions,
  passengerTypeOptions,
} from "@/constants";
import { FLIGHT_DATA_FAST_FORWARD } from "@/constants/eventName";
import { megaProgramsOptions } from "@/constants/mega";
import { searchProgramsOptions } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import { useListenEvent } from "@/hooks/useEventEmitter";
import usePlatform from "@/hooks/usePlatform";
import useSearchCondition, { SearchConditionType } from "@/hooks/useSearchCondition";

import AlertModal, { AlertModalRefProps } from "../AlertModal";
import TrackPriceCardPure from "../TrackPriceCardPure";
import SearchAirport from "./SearchAirport";
import SearchCheckboxPrograms from "./SearchCheckboxPrograms";
import SearchDate from "./SearchDate";
import SearchRangeDate from "./SearchRangeDate";
import SearchSelect from "./SearchSelect";
import SearchSwitch from "./SearchSwitch";

const flightWayIconMap: Record<string, typeof SvgIcon> = {
  [FlightWayEnum.OneWay]: ArrowRightAltIcon,
  [FlightWayEnum.RoundTrip]: SyncAltIcon,
};

interface SearchBarProps {
  isLoading?: boolean;
  withSearchBtn?: boolean;
  withMobileSearchBtn?: boolean;
  onSearch?: (data: SearchConditionType) => void;
  onChange?: (data: SearchConditionType) => void;
  getExtraData?: () => any;
}

type RangeDate = [Dayjs | null, Dayjs | null];

const promot_style = { fontWeight: "bold" };
const SearchBar = ({
  onChange,
  onSearch,
  getExtraData,
  withSearchBtn = false,
  withMobileSearchBtn = false,
  isLoading = false,
}: SearchBarProps) => {
  const { isMobile } = usePlatform();

  const {
    searchLimit,
    condition,
    setCondition,
    onLocationSwapChange,
    onSwitchChange,
    onOneWayDateChange,
    onOneWayRangeDateChange,
    onRoundTripDepartureDateChange,
    onRoundTripReturnDateChange,
    onDepartureRangeChange,
    onReturnRangeChange,
  } = useSearchCondition();

  const [trackPriceOpen, setTrackPriceOpen] = useState(false);

  const modalRef = useRef<AlertModalRefProps>(null);

  const store = useGlobalStoreContext();
  const disableSwap = useMemo(() => {
    return !condition.fromLocation.length || !condition.toLocation.length;
  }, [condition.fromLocation, condition.toLocation]);

  const roundTrip = useMemo(
    () => condition.flightWay === FlightWayEnum.RoundTrip,
    [condition.flightWay]
  );

  const datePickerRef = useRef<{ handleChange: (v: RangeDate) => void }>(null);

  const isFastForwardRef = useRef(false);

  useEffect(() => {
    onChange && onChange(condition);
  }, [condition, onChange]);

  useListenEvent(FLIGHT_DATA_FAST_FORWARD, (v: RangeDate) => {
    datePickerRef.current?.handleChange(v);
    isFastForwardRef.current = true;
  });

  useEffect(() => {
    if (isFastForwardRef.current) {
      isFastForwardRef.current = false;
      onSearch && onSearch(condition);
    }
  }, [condition, onSearch]);

  const SearchBtn = useMemo(() => {
    return withSearchBtn ? (
      <div className="flex items-center justify-end mt-[15px] ml-[10px] absolute right-0">
        <LoadingButton
          color="primary"
          loading={isLoading}
          variant="contained"
          className="!min-w-[48px] !h-[56px] !w-[48px]"
          onClick={() => onSearch && onSearch(condition)}
        >
          <SearchIcon sx={{ fontSize: "24px" }} />
        </LoadingButton>
      </div>
    ) : null;
  }, [isLoading, withSearchBtn, onSearch, condition]);

  const isBigScreen = useMediaQuery("(min-width:1200px)");

  const airportSmGridItem = useMemo(() => {
    if (roundTrip) {
      return withSearchBtn && isBigScreen ? 6 : 7.5;
    } else {
      return withSearchBtn && isBigScreen ? 8 : 7.5;
    }
  }, [roundTrip, withSearchBtn, isBigScreen]);

  const dateSmGridItem = 12 - airportSmGridItem;
  const roundTripDateSmGridItem = withSearchBtn && isBigScreen ? 6 : 12;
  const roundTripRange = !(searchLimit.daterange_dep <= 1 && searchLimit.daterange_arrival <= 1);
  return (
    <div>
      <div className="relative flex items-center justify-start w-full py-3 overflow-hidden">
        <SearchSelect
          value={condition.flightWay}
          options={flightWayOptions}
          Icon={flightWayIconMap[condition.flightWay]}
          onChange={(data) => {
            setCondition({ ...condition, flightWay: data });
          }}
        />

        <SelectPassengerV2
          min={passengerTypeOptions[0].value}
          max={passengerTypeOptions[passengerTypeOptions.length - 1].value}
          value={condition.pax}
          onChange={(data) => {
            setCondition({ ...condition, pax: data });
          }}
          childValue={condition.children}
          onChangeChild={(data) => {
            setCondition({ ...condition, children: data });
          }}
        />

        <SearchSelect
          value={condition.cabins}
          options={cabinOptionMap}
          Icon={isMobile ? null : FlightClassOutlinedIcon}
          onChange={(data) => {
            setCondition({ ...condition, cabins: data });
          }}
        />

        <SearchCheckboxPrograms
          value={condition.programs}
          Icon={isMobile ? null : CardMembershipIcon}
          options={searchProgramsOptions.map((item) => ({
            value: item.value,
            label: item.fullname,
          }))}
          onChange={(data) => {
            setCondition({ ...condition, programs: data });
          }}
        />

        {!isMobile ? (
          <div className="flex justify-end flex-1">
            <TrackPriceCardPure
              value={trackPriceOpen}
              onChange={(value) => {
                setTrackPriceOpen(value);
                if (value) {
                  modalRef.current &&
                    modalRef.current.show(AlertTypeEnum.Flight, getExtraData?.() ?? {});
                }
              }}
            />
            <SearchSwitch
              showUpsell={store?.persistStore?.show_upsell}
              value={condition.rangeV2}
              onChange={onSwitchChange}
            />
          </div>
        ) : null}
      </div>

      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        className="relative"
        sx={{
          paddingRight: withSearchBtn ? "55px" : 0,
          paddingBottom: "8px",
        }}
      >
        <Grid item xs={12} sm={airportSmGridItem}>
          <Grid container alignItems="center" rowGap={2}>
            <Grid item xs={12} sm={5.6}>
              <SearchAirport
                placeholder="Where from?"
                Icon={<FlightTakeoffOutlinedIcon />}
                selectData={condition.fromLocation}
                onChange={(data) => {
                  setCondition({ ...condition, fromLocation: data });
                }}
                maxCount={searchLimit.choice_count_dep}
              />
            </Grid>

            {!isMobile ? (
              <Grid item xs={1} sm={0.8} alignItems="center">
                <div className="flex items-center justify-center">
                  <IconButton
                    aria-label="swap"
                    size="small"
                    onClick={onLocationSwapChange}
                    disabled={disableSwap}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </div>
              </Grid>
            ) : (
              <div className={cn("absolute right-6 z-10", { hidden: disableSwap })}>
                <Fab color="primary" aria-label="swap" size="small" onClick={onLocationSwapChange}>
                  <SwapHorizIcon className="rotate-90" />
                </Fab>
              </div>
            )}

            <Grid item xs={12} sm={5.6}>
              <SearchAirport
                placeholder="Where to?"
                Icon={<FlightLandOutlinedIcon />}
                selectData={condition.toLocation}
                onChange={(data) => {
                  setCondition({ ...condition, toLocation: data });
                }}
                maxCount={searchLimit.choice_count_arrival}
              />
            </Grid>
          </Grid>
        </Grid>

        {!roundTrip ? (
          <Grid item xs={12} sm={dateSmGridItem}>
            <div className="flex">
              {searchLimit.daterange_dep > 1 ? (
                <SearchRangeDate
                  ref={datePickerRef}
                  key="oneWayRangeDate"
                  label="Departure Date Range"
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  maxRangeDays={searchLimit.daterange_dep}
                  defaultValue={[
                    condition.oneWayRangeStartDate ? dayjs(condition.oneWayRangeStartDate) : null,
                    condition.oneWayRangeEndDate ? dayjs(condition.oneWayRangeEndDate) : null,
                  ]}
                  onChange={onOneWayRangeDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px] px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              ) : (
                <SearchDate
                  key="oneWayDate"
                  label="Departure Date"
                  defaultValue={condition.oneWayDate ? dayjs(condition.oneWayDate) : undefined}
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  onChange={onOneWayDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px] px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              )}
            </div>
          </Grid>
        ) : null}

        {roundTrip && !roundTripRange ? (
          <Grid item xs={12} sm={dateSmGridItem}>
            <Grid container alignItems="center" rowGap={2} columnSpacing={1}>
              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchDate
                  key="departureDate"
                  label="Departure Date"
                  defaultValue={
                    condition.roundTripDepartureDate
                      ? dayjs(condition.roundTripDepartureDate)
                      : undefined
                  }
                  onChange={onRoundTripDepartureDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchDate
                  key="returnDate"
                  label="Return Date"
                  defaultValue={
                    condition.roundTripReturnDate ? dayjs(condition.roundTripReturnDate) : undefined
                  }
                  minDate={
                    condition.roundTripDepartureDate
                      ? dayjs(condition.roundTripDepartureDate)
                      : undefined
                  }
                  onChange={onRoundTripReturnDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_arrival + " days"}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        ) : null}

        {roundTrip && roundTripRange ? (
          <Grid item xs={12} sm={dateSmGridItem}>
            <Grid container alignItems="center" rowGap={2} columnSpacing={1}>
              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchRangeDate
                  key="departureRangeDate"
                  defaultValue={[
                    condition.departureRangeStartDate
                      ? dayjs(condition.departureRangeStartDate)
                      : null,
                    condition.departureRangeEndDate ? dayjs(condition.departureRangeEndDate) : null,
                  ]}
                  // maxRangeDays={7}
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  maxRangeDays={searchLimit.daterange_dep}
                  label="Departure Date Range"
                  onChange={onDepartureRangeChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchRangeDate
                  key="returnRangeDate"
                  defaultValue={[
                    condition.returnRangeStartDate ? dayjs(condition.returnRangeStartDate) : null,
                    condition.returnRangeEndDate ? dayjs(condition.returnRangeEndDate) : null,
                  ]}
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  maxRangeDays={searchLimit.daterange_arrival}
                  label="Return Date Range"
                  onChange={onReturnRangeChange}
                  minDate={
                    condition.departureRangeStartDate
                      ? dayjs(condition.departureRangeStartDate)
                      : undefined
                  }
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_arrival + " days"}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        ) : null}

        {SearchBtn}
      </Grid>

      {isMobile ? (
        <div className="pt-2">
          <SearchSwitch
            showUpsell={store?.show_upsell}
            value={condition.rangeV2}
            onChange={onSwitchChange}
          />
        </div>
      ) : null}

      {withMobileSearchBtn ? (
        <div className="pb-[18px] pt-[10px]">
          <LoadingButton
            variant="contained"
            className="w-full"
            loading={isLoading}
            startIcon={<SearchIcon />}
            onClick={() => onSearch && onSearch(condition)}
          >
            Search
          </LoadingButton>
        </div>
      ) : null}

      <AlertModal ref={modalRef} onReset={() => setTrackPriceOpen(false)} />
    </div>
  );
};

export default SearchBar;
